// "Remind me" as a real notification where the browser can do it: Chrome/Firefox/Edge, and iPhone
// once parking is on the Home Screen. Everywhere else the caller falls back to a calendar event.

export const canPush = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

const bytes = (b64url) => Uint8Array.from(atob(b64url.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0))

/** Schedule a push 15 minutes before `at` (the move-by time, or the meter coming back on). Resolves {id, at} or null if push isn't possible here. */
export async function remindPush(args) {
  if (!canPush()) return null
  // Ask first, while we're still inside the tap (Safari only allows the prompt from a user gesture).
  // The prompt waits on the person, so it isn't timed.
  if ((await Notification.requestPermission()) !== 'granted') return null
  // Some browsers (private windows, missing push service) let subscribe() hang: give up and fall
  // back. If it finishes after that, cancel what it scheduled so no reminder is left we can't cancel.
  let gaveUp = false
  const job = subscribeAndSchedule(args).then((r) => { if (gaveUp && r) cancelPush(r.id); return r })
  return Promise.race([job, new Promise((r) => setTimeout(() => { gaveUp = true; r(null) }, 8000))])
}

async function subscribeAndSchedule({ at, street, why, when, meter, moveBy }) {
  const { key } = await fetch('/api/push/key').then((r) => r.json()).catch(() => ({}))
  if (!key) return null
  const reg = await navigator.serviceWorker.ready
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytes(key) }))
  const j = sub.toJSON()
  const res = await fetch('/api/remind', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sub: { endpoint: j.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth }, at, street, why, when, meter, moveBy }),
  })
  if (!res.ok) return null
  return res.json()
}

// "I've left" underground: a cancel that couldn't reach us is kept and sent once there's signal, or
// the reminder would still go off.
const PENDING = 'parking:cancel'
const pending = () => { try { return JSON.parse(localStorage.getItem(PENDING) || '[]') } catch { return [] } }
const setPending = (ids) => { try { ids.length ? localStorage.setItem(PENDING, JSON.stringify(ids)) : localStorage.removeItem(PENDING) } catch {} }

export function cancelPush(id) {
  if (!id) return
  // Queued first, cleared once the server has it: closing the app mid-request can't lose it.
  setPending([...new Set([...pending(), id])])
  fetch(`/api/remind?id=${encodeURIComponent(id)}`, { method: 'DELETE', keepalive: true, signal: AbortSignal.timeout(10000) })
    .then((r) => { if (r.status < 500) setPending(pending().filter((x) => x !== id)) }) // a 4xx won't get better
    .catch(() => {}) // stays queued
}

/** Send any cancels that didn't get through (on start, and when signal comes back). */
export function retryCancels() {
  for (const id of pending()) cancelPush(id)
}

export function registerWorker() {
  if ('serviceWorker' in navigator) navigator.serviceWorker.register('/sw.js').catch(() => {})
}
